/**
 * @file 权限状态管理
 */

import {create} from 'zustand';
import type {PermissionCode} from '@/types/permission';
import type {ProjectId} from '@/types';

interface PermissionsState {
    permissionsByProject: Record<ProjectId, Set<PermissionCode>>;
    roleCodesByProject: Record<ProjectId, string[]>;
    currentProjectId: ProjectId | null;
    isLoading: boolean;
    error: string | null;
}

interface PermissionsActions {
    setPermissions: (projectId: ProjectId, codes: PermissionCode[], roleCodes: string[]) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    clearPermissions: (projectId?: ProjectId) => void;
    setCurrentProjectId: (projectId: ProjectId | null) => void;
}

export const usePermissionsStore = create<PermissionsState & PermissionsActions>((set) => ({
    permissionsByProject: {},
    roleCodesByProject: {},
    currentProjectId: null,
    isLoading: true, // 初始为 true，等待首次权限请求完成后才置 false
    error: null,

    setCurrentProjectId: (projectId: ProjectId | null) => {
        set({currentProjectId: projectId});
    },

    setPermissions: (projectId: ProjectId, codes: PermissionCode[], roleCodes: string[]) => {
        set((state) => ({
            currentProjectId: projectId,
            permissionsByProject: {
                ...state.permissionsByProject,
                [projectId]: new Set(codes),
            },
            roleCodesByProject: {
                ...state.roleCodesByProject,
                [projectId]: roleCodes,
            },
            isLoading: false,
            error: null,
        }));
    },

    setLoading: (loading: boolean) => {
        set({isLoading: loading});
    },

    setError: (error: string | null) => {
        set({error, isLoading: false});
    },

    clearPermissions: (projectId?: ProjectId) => {
        set((state) => {
            if (projectId) {
                const newPermissions = {...state.permissionsByProject};
                delete newPermissions[projectId];
                const newRoleCodes = {...state.roleCodesByProject};
                delete newRoleCodes[projectId];
                return {
                    permissionsByProject: newPermissions,
                    roleCodesByProject: newRoleCodes,
                    currentProjectId:
                        state.currentProjectId === projectId ? null : state.currentProjectId,
                    isLoading: true, // 重置后等待新项目权限请求
                    error: null,
                };
            }
            return {
                permissionsByProject: {},
                roleCodesByProject: {},
                currentProjectId: null,
                isLoading: true, // 重置后等待新项目权限请求
                error: null,
            };
        });
    },
}));
